import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  ProcessExecutionType,
  ProcessFlowchartNodeDetailsType,
  ProcessSingletonTaskSchemaName,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {getIncrementedNodeName} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/nodeUtils';
import {
  NodeCreationContext,
  ProcessFlowchartTaskSchemaNodeAdapter,
  TaskSchemaDisplayOptions,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {
  NodeSettingsTabKeys,
  ProcessTaskDefinitionVersionGraphQL,
  SidepanelTab,
  TaskSchemaNodeSectionType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  TaskDefinitionVersionSpecWithTaskSchema,
  TaskSchemaNodeSectionData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import getIconAndLabel from 'registry/field/GetFieldLabelAndIcon';
import {getSchemaFieldTypeAndMultiFromVariableSpec} from 'results/analysis-integration/AnalysisTemplateIntegrationUtils';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {SidepanelSize} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import PipelineNodeSidepanelContent from './PipelineNodeSidepanelContent';

export type AnalysisTemplateGql = ExtractByTypename<
  ProcessTaskDefinitionVersionGraphQL['applicationData'],
  'AnalysisTemplateNode'
>;

export default class PipelineNodeAdapter extends ProcessFlowchartTaskSchemaNodeAdapter<
  AnalysisTemplateGql,
  object
> {
  readonly executionType: ProcessExecutionType = ProcessExecutionType.PIPELINE;

  readonly typeDisplayName: string = 'pipeline';

  nodeDetailsSidepanelContent: Array<SidepanelTab> = [
    {
      key: NodeSettingsTabKeys.TASK_SCHEMA_DETAILS,
      label: 'Pipeline',
      content: PipelineNodeSidepanelContent,
    },
  ];

  convertTaskDefinitionVersionGQLToApplicationData(
    taskDefinitionVersionGQL: ProcessTaskDefinitionVersionGraphQL
  ): AnalysisTemplateGql {
    // TODO: This is currently a no-op. We just return the GQL shape so we can parse the config variables to
    // render sections. Once we have designs for the pipeline node sidepanel, we should generate an actual
    // frontend application data spec and do the conversion here.
    const applicationData = taskDefinitionVersionGQL.applicationData;
    assert(applicationData != null, 'Application data is required');
    assert(
      applicationData.__typename === 'AnalysisTemplateNode',
      'Application data is not an analysis template node'
    );
    return applicationData;
  }

  convertTaskDefinitionVersionSpecToNetworkApplicationData(
    taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema<AnalysisTemplateGql>,
    flowchartConfigId: string
  ): object {
    // TODO: This just takes the existing GQL spec and passes the ID back to the API.
    assert(
      taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.applicationData != null,
      'Application data is required'
    );
    return {analysisTemplateId: taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.applicationData.id};
  }

  /**
   * Extracts the display name for a procedure node from its task definition version.
   * Expects the application data to be an AnalysisTemplate.
   *
   * @param taskDefinitionVersion - The task definition version to extract name from
   * @returns The display name for the procedure node
   * @throws Error if applicationData is null or not a ProcedureMethodDefinitionVersionNode
   */
  getNodeName(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<AnalysisTemplateGql>,
    isFlowchartOutput?: boolean,
    existingNodeNames?: Array<string>
  ): string {
    const applicationData = taskDefinitionVersion.newProcessTaskDefinitionVersion.applicationData;

    assert(
      applicationData != null,
      `applicationData is required for task definition version ${taskDefinitionVersion.clientId}`
    );

    return getIncrementedNodeName(applicationData.name, existingNodeNames);
  }

  getNodeSubtitle(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<AnalysisTemplateGql>,
    isFlowchartOutput?: boolean
  ): string | null {
    return null;
  }

  getNodeIcon(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<AnalysisTemplateGql>,
    isFlowchartOutput?: boolean
  ): BenchlingIconName | null {
    return ModelTypeConstants.ANALYSIS;
  }

  /**
   * Creates section data for rendering the pipeline node's field groups.
   *
   * @param taskDefinitionVersion - The task definition version containing field definitions
   * @param nodeDetailsType - The type of node details (unused in this implementation)
   * @returns Array of section data for the task schema node renderer
   */
  createSections(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<AnalysisTemplateGql>,
    nodeDetailsType: ProcessFlowchartNodeDetailsType
  ): Array<TaskSchemaNodeSectionData> {
    const applicationData = taskDefinitionVersion.newProcessTaskDefinitionVersion.applicationData;
    if (applicationData == null) {
      return [];
    }

    return [
      {
        sectionName: TaskSchemaNodeSectionType.MAP_FROM_START,
        sectionType: TaskSchemaNodeSectionType.MAP_FROM_START,
        sectionRows: [],
      },
      {
        sectionName: TaskSchemaNodeSectionType.INPUTS,
        sectionType: TaskSchemaNodeSectionType.INPUTS,
        sectionRows: applicationData.configVariables.map((configVariable) => {
          const fieldTypeAndMulti = getSchemaFieldTypeAndMultiFromVariableSpec(
            configVariable.variableTypeConfigSpec
          );
          const fieldType = fieldTypeAndMulti ? fieldTypeAndMulti.fieldType : FieldType.TEXT;
          const {icon, label} = getIconAndLabel({
            field: {
              fieldType,
            },
            schemaFieldSelectors: [],
          });
          // Some config variables - specifically ones that are added automatically as part of "output" steps
          // do not have a name set. In those cases we use the config variable key as the field name.
          const configName = configVariable.name.trim();
          const fieldName = configName.length > 0 ? configName : configVariable.configVariableKey;
          return {
            id: configVariable.id,
            clientId: configVariable.id,
            fieldName,
            fieldIcon: icon,
            fieldTypeLabel: label,
            sectionName: TaskSchemaNodeSectionType.INPUTS,
            sectionType: TaskSchemaNodeSectionType.INPUTS,
            isFlowchartTask: false,
          };
        }),
      },
    ];
  }

  onCreateNewNode(
    context: NodeCreationContext
  ): Promise<TaskDefinitionVersionSpecWithTaskSchema<AnalysisTemplateGql> | null> {
    throw new Error('Not implemented');
  }

  getNewNodeDetails(context: NodeCreationContext): {
    nodeName: string;
    nodeSubtitle: string | null;
    nodeIcon: BenchlingIconName | null;
  } {
    return {
      nodeName: 'New analysis',
      nodeSubtitle: null,
      nodeIcon: null,
    };
  }

  getSingletonTaskSchemaName(
    rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL
  ): ProcessSingletonTaskSchemaName | null {
    return ProcessSingletonTaskSchemaName.PIPELINE_TASK_SCHEMA_NAME;
  }

  override getDisplayOptions(): TaskSchemaDisplayOptions {
    return {rightSidepanelSize: SidepanelSize.MEDIUM};
  }
}
